/**
 * AutoResume Cookie Utility
 * 
 * Provides secure cookie-based mechanism for autoResume feature.
 * Cookies are encrypted on write and decrypted on read to prevent tampering.
 * Uses a read-and-delete pattern for one-time use.
 */

const COOKIE_KEY = 'mnb_ar';
const ENCRYPTION_KEY = 'M4n4b4kh_s3cur3_k3y_2024'; // Custom key for obfuscation

/**
 * Simple XOR encryption with base64 encoding
 * Not cryptographically secure but makes tampering difficult
 */
const encrypt = (text) => {
    try {
        const timestamp = Date.now().toString(36);
        const payload = `${timestamp}:${text}`;
        let result = '';
        for (let i = 0; i < payload.length; i++) {
            const charCode = payload.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
            result += String.fromCharCode(charCode);
        }
        // Base64 encode and make URL safe
        return btoa(result).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
    } catch (e) {
        console.error('[AutoResume] Encryption failed:', e);
        return null;
    }
};

/**
 * Decrypt the encrypted cookie value
 */
const decrypt = (encrypted) => {
    try {
        // Restore base64 padding
        let base64 = encrypted.replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4) base64 += '=';

        const decoded = atob(base64);
        let result = '';
        for (let i = 0; i < decoded.length; i++) {
            const charCode = decoded.charCodeAt(i) ^ ENCRYPTION_KEY.charCodeAt(i % ENCRYPTION_KEY.length);
            result += String.fromCharCode(charCode);
        }

        // Extract timestamp and validate (cookie valid for 5 minutes max)
        const [timestamp, value] = result.split(':');
        const cookieTime = parseInt(timestamp, 36);
        const isValid = Date.now() - cookieTime < 300000; // 5 minutes

        return isValid ? value : null;
    } catch (e) {
        console.error('[AutoResume] Decryption failed:', e);
        return null;
    }
};

/**
 * Set autoResume cookie with encrypted payload
 * @param {Object} payload - { slug, season?, episode? }
 */
export const setAutoResumeCookie = (payload) => {
    try {
        const value = encrypt(JSON.stringify(payload));
        if (!value) return;

        // Short expiry (5 minutes), HttpOnly not possible in JS, SameSite for security
        document.cookie = `${COOKIE_KEY}=${value}; path=/; max-age=300; SameSite=Strict`;
        console.log('[AutoResume] Cookie set for:', payload.slug);
    } catch (e) {
        console.error('[AutoResume] Failed to set cookie:', e);
    }
};

/**
 * Read and delete autoResume cookie (one-time use)
 * @returns {Object|null} - { slug, season?, episode? } or null
 */
export const consumeAutoResumeCookie = () => {
    try {
        const cookies = document.cookie.split(';');
        let cookieValue = null;

        for (const cookie of cookies) {
            const [key, value] = cookie.trim().split('=');
            if (key === COOKIE_KEY) {
                cookieValue = value;
                break;
            }
        }

        if (!cookieValue) return null;

        // Delete cookie immediately after reading
        document.cookie = `${COOKIE_KEY}=; path=/; max-age=0`;

        // Decrypt and parse
        const decrypted = decrypt(cookieValue);
        if (!decrypted) return null;

        const payload = JSON.parse(decrypted);
        console.log('[AutoResume] Cookie consumed for:', payload.slug);
        return payload;
    } catch (e) {
        console.error('[AutoResume] Failed to consume cookie:', e);
        return null;
    }
};

/**
 * Check if content matches the autoResume cookie
 * @param {string} slug - Content slug
 * @param {string|null} season - Season number
 * @param {string|null} episode - Episode number
 * @returns {boolean}
 */
export const checkAutoResumeMatch = (slug, season, episode) => {
    const payload = consumeAutoResumeCookie();
    if (!payload) return false;

    // For non-series content, just match slug
    if (!payload.season && !payload.episode) {
        return payload.slug === slug;
    }

    // For series, match slug + season + episode
    return payload.slug === slug &&
        payload.season === season &&
        payload.episode === episode;
};

const autoResumeCookie = {
    setAutoResumeCookie,
    consumeAutoResumeCookie,
    checkAutoResumeMatch,
};

export default autoResumeCookie;
